import React, { useState } from "react";
import { Link } from "react-router-dom";
import MarketingShell from "./MarketingShell";
import Seo from "./Seo";
import QuietWallPreview from "./QuietWallPreview";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Heart, ArrowRight, Sparkles } from "lucide-react";

const ONE_TIME = [
  { id: "d5",  amount: 5,  hint: "A small gift" },
  { id: "d15", amount: 15, hint: "Most chosen" },
  { id: "d30", amount: 30, hint: "Deep care" },
  { id: "d100", amount: 100, hint: "A guardian gift" },
];
const MONTHLY = [
  { id: "m5",  amount: 5,  hint: "A quiet patron" },
  { id: "m10", amount: 10, hint: "A steady hand" },
  { id: "m25", amount: 25, hint: "A guardian gift" },
];

export default function DonateMarketing() {
  const [mode, setMode] = useState("one_time");
  const [preset, setPreset] = useState("d15");
  const [custom, setCustom] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const tiers = mode === "monthly" ? MONTHLY : ONE_TIME;
  const selected = tiers.find((t) => t.id === preset);
  const amount = custom ? Math.max(1, Math.round(Number(custom))) : (selected?.amount || 15);

  const donate = async () => {
    setBusy(true);
    try {
      // Backend (DonationIn) wants:
      //   mode: "one_time" | "monthly"
      //   origin_url: <window origin used to build success/cancel URLs>
      //   preset | custom_amount  (exactly one)
      //   email?, honoree_name?, dedication_note?, donor_label?, share_publicly?
      // The marketing tiers d5/d15/d30 and m5/m10/m25 match backend presets.
      // d100 is intentionally NOT a backend preset; we send it as custom.
      const isCustomPreset = preset === "d100";
      const body = {
        mode: mode === "monthly" ? "monthly" : "one_time",
        origin_url: window.location.origin,
        email: email || undefined,
      };
      if (custom) {
        body.custom_amount = parseFloat(custom);
      } else if (isCustomPreset) {
        body.custom_amount = selected?.amount || 100;
      } else {
        body.preset = preset;
      }
      const { data } = await api.post("/donations/checkout", body);
      if (data?.url) window.location.href = data.url;
      else toast.error("Couldn't start checkout — please try again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Donation failed to initiate.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MarketingShell>
      <Seo title="Donate" description="Sister Signal is a 501(c)(3). Donations keep the app free for survivors. EIN 42-3100694." path="/donate" />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 grid lg:grid-cols-[1fr_minmax(0,360px)] gap-10" data-testid="page-donate-grid">
        <article className="min-w-0" data-testid="page-donate">
        <p className="font-script text-2xl text-brand">keep clarity free</p>
        <h1 className="mt-2 font-serif text-5xl sm:text-6xl leading-tight">Donate.</h1>
        <p className="mt-5 text-lg text-foreground/75 leading-relaxed">
          Sister Signal is free for every survivor who needs it. Donations from people like you keep
          it that way — and let us reach the next person quietly asking <em>am I crazy or is this real?</em>
        </p>

        <div className="mt-10 rounded-3xl bg-card border border-border p-7" data-testid="donate-card">
          <div className="inline-flex rounded-full bg-brand-soft/40 p-1" role="tablist">
            <button data-testid="donate-mode-one-time"
              onClick={() => { setMode("one_time"); setPreset("d15"); }}
              className={`px-5 py-2 rounded-full text-sm transition-all ${mode === "one_time" ? "bg-brand text-white" : "text-foreground/70"}`}>
              One-time
            </button>
            <button data-testid="donate-mode-monthly"
              onClick={() => { setMode("monthly"); setPreset("m10"); }}
              className={`px-5 py-2 rounded-full text-sm transition-all ${mode === "monthly" ? "bg-brand text-white" : "text-foreground/70"}`}>
              Monthly
            </button>
          </div>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {tiers.map((t) => {
              const active = preset === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`donate-preset-${t.id}`}
                  onClick={() => { setPreset(t.id); setCustom(""); }}
                  className={`rounded-2xl p-4 text-left border transition-all ${
                    active ? "border-brand bg-brand/8" : "border-border bg-card hover:border-brand/40"
                  }`}
                >
                  <div className="font-serif text-xl">${t.amount}{mode === "monthly" ? "/mo" : ""}</div>
                  <div className="text-xs text-foreground/55">{t.hint}</div>
                </button>
              );
            })}
          </div>

          <label className="block mt-5">
            <span className="block text-xs uppercase tracking-widest text-foreground/55 mb-1.5">Or a custom amount</span>
            <div className="flex items-center gap-2">
              <span className="text-foreground/55">$</span>
              <input
                data-testid="donate-custom"
                inputMode="numeric"
                value={custom}
                onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="Any amount"
                className="flex-1 h-12 rounded-2xl bg-background border border-border px-4"
              />
            </div>
          </label>

          <label className="block mt-3">
            <span className="block text-xs uppercase tracking-widest text-foreground/55 mb-1.5">Email (for receipt)</span>
            <input data-testid="donate-email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="optional"
              className="w-full h-12 rounded-2xl bg-background border border-border px-4" />
          </label>

          <button
            onClick={donate}
            disabled={busy || amount < 1}
            data-testid="donate-submit"
            className="w-full mt-6 h-14 rounded-full bg-brand text-white hover:bg-brand/90 transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            <Heart className="w-4 h-4" />
            {busy ? "Opening checkout…" : `Give $${amount}${mode === "monthly" ? "/mo" : ""}`}
            <Sparkles className="w-4 h-4" />
          </button>
          <p className="mt-3 text-xs text-foreground/55 text-center">
            Secure payment via Stripe. You&rsquo;ll be redirected to complete checkout.
          </p>
        </div>

        <section className="mt-10 rounded-3xl bg-brand-soft/40 border border-brand/20 p-7">
          <p className="font-script text-xl text-brand">other ways to support</p>
          <ul className="mt-3 space-y-2 text-foreground/80">
            <li>· Sponsorships and corporate gifts (<Link to="/partners" className="text-brand hover:underline">contact us</Link>)</li>
            <li>· Grant introductions and foundation partnerships</li>
            <li>· Sharing Sister Signal with someone who might need it</li>
            <li>· Following us on <Link to="/" className="text-brand hover:underline">social</Link> and amplifying survivor voices</li>
          </ul>
        </section>

        <Link to="/" className="mt-8 inline-flex items-center gap-1 text-brand hover:underline">
          Back to home <ArrowRight className="w-4 h-4" />
        </Link>
      </article>
        <div className="lg:sticky lg:top-24 self-start">
          <QuietWallPreview />
        </div>
      </div>
    </MarketingShell>
  );
}
